import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { Star, Check, X, Trash2, Send, Clock, Eye, Search, UserPlus, ChevronDown } from 'lucide-react';

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className="h-4 w-4" fill={s <= rating ? '#FF8C19' : 'none'} style={{ color: s <= rating ? '#FF8C19' : '#e5d5c0' }} />
    ))}
  </div>
);

const CustomerPicker = ({ onSelect, onManualMode }) => {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    Promise.all([
      fetch('/api/admin/customers', { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json()),
      fetch('/api/admin/orders', { headers: { 'Authorization': `Bearer ${token}` } }).then(r => r.json())
    ]).then(([custs, ords]) => {
      setCustomers(custs || []);
      setOrders(ords || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Build merged list: customers with their order numbers
  const customerList = React.useMemo(() => {
    const map = new Map();
    // Add from customers collection
    customers.forEach(c => {
      map.set(c.email, { name: c.name || '', email: c.email, orders: [] });
    });
    // Add from orders and attach order numbers
    orders.forEach(o => {
      const ci = o.customerInfo || {};
      const email = ci.email;
      if (!email) return;
      const name = `${ci.firstName || ''} ${ci.lastName || ''}`.trim();
      if (!map.has(email)) {
        map.set(email, { name, email, orders: [] });
      }
      const entry = map.get(email);
      if (!entry.name && name) entry.name = name;
      if (o.orderNumber) entry.orders.push(o.orderNumber);
    });
    return Array.from(map.values());
  }, [customers, orders]);

  const filtered = search.trim()
    ? customerList.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase()) ||
        c.orders.some(o => o.toLowerCase().includes(search.toLowerCase()))
      )
    : customerList;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <p className="text-sm font-medium" style={{ color: '#4a2c1a' }}>Select a customer or</p>
        <button
          data-testid="manual-entry-btn"
          onClick={onManualMode}
          className="text-sm font-semibold underline flex items-center gap-1"
          style={{ color: '#FF8C19' }}
        >
          <UserPlus className="h-3.5 w-3.5" /> enter manually
        </button>
      </div>
      <div className="relative" ref={ref}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#736c64' }} />
          <Input
            data-testid="customer-search-input"
            placeholder={loading ? 'Loading customers...' : 'Search by name, email, or order #...'}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            className="pl-9 pr-8"
          />
          <ChevronDown
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 cursor-pointer"
            style={{ color: '#736c64' }}
            onClick={() => setOpen(!open)}
          />
        </div>
        {open && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ borderColor: '#e5d5c0' }}>
            {filtered.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-sm" style={{ color: '#736c64' }}>
                  {loading ? 'Loading...' : 'No customers found'}
                </p>
                {!loading && (
                  <button onClick={onManualMode} className="text-sm font-semibold mt-1 underline" style={{ color: '#FF8C19' }}>
                    Enter details manually
                  </button>
                )}
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.email}
                  data-testid={`customer-option-${c.email}`}
                  className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors border-b last:border-b-0"
                  style={{ borderColor: '#f0ebe5' }}
                  onClick={() => {
                    onSelect(c);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-sm" style={{ color: '#4a2c1a' }}>{c.name || '(No name)'}</p>
                      <p className="text-xs" style={{ color: '#736c64' }}>{c.email}</p>
                    </div>
                    {c.orders.length > 0 && (
                      <div className="text-right">
                        <p className="text-xs font-medium" style={{ color: '#FF8C19' }}>
                          {c.orders.length} order{c.orders.length > 1 ? 's' : ''}
                        </p>
                        <p className="text-xs" style={{ color: '#736c64' }}>#{c.orders[0]}</p>
                      </div>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', customerName: '', orderNumber: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filter, setFilter] = useState('all');
  const { toast } = useToast();

  useEffect(() => { fetchReviews(); }, []);

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/reviews', { headers: { 'Authorization': `Bearer ${token}` } });
      setReviews(await res.json());
    } catch {}
    finally { setLoading(false); }
  };

  const approveReview = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`/api/admin/reviews/${id}/approve`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } });
      toast({ title: "Approved!", description: "Review is now visible on the site." });
      fetchReviews();
    } catch { toast({ title: "Error", variant: "destructive" }); }
  };

  const rejectReview = async (id) => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`/api/admin/reviews/${id}/reject`, { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } });
      toast({ title: "Rejected", description: "Review hidden from site." });
      fetchReviews();
    } catch { toast({ title: "Error", variant: "destructive" }); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`/api/admin/reviews/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      toast({ title: "Deleted" });
      fetchReviews();
    } catch { toast({ title: "Error", variant: "destructive" }); }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setEmailData({
      customerName: customer.name,
      email: customer.email,
      orderNumber: customer.orders.length > 0 ? customer.orders[0] : ''
    });
  };

  const resetEmailForm = () => {
    setEmailData({ email: '', customerName: '', orderNumber: '' });
    setSelectedCustomer(null);
    setManualMode(false);
  };

  const sendReviewRequest = async () => {
    if (!emailData.email || !emailData.customerName) return;
    setSendingEmail(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/reviews/send-request', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(emailData)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast({ title: "Sent!", description: data.message });
      resetEmailForm();
      setShowEmailForm(false);
    } catch (err) { toast({ title: "Error", description: err.message, variant: "destructive" }); }
    finally { setSendingEmail(false); }
  };

  const filtered = filter === 'all' ? reviews
    : filter === 'pending' ? reviews.filter(r => !r.approved)
    : reviews.filter(r => r.approved);

  const pendingCount = reviews.filter(r => !r.approved).length;

  if (loading) return <p className="text-center py-10" style={{ color: '#736c64' }}>Loading reviews...</p>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>Reviews</h2>
          <p className="text-sm" style={{ color: '#736c64' }}>
            {reviews.length} total · {pendingCount} pending approval
          </p>
        </div>
        <Button
          data-testid="send-review-request-btn"
          onClick={() => { setShowEmailForm(!showEmailForm); if (showEmailForm) resetEmailForm(); }}
          className="text-white font-semibold"
          style={{ backgroundColor: '#FF8C19' }}
        >
          <Send className="h-4 w-4 mr-2" /> Request Review
        </Button>
      </div>

      {/* Review Request Email Form */}
      {showEmailForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4" style={{ borderColor: '#FF8C19' }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: '#4a2c1a' }}>Send Review Request Email</h3>

          {/* Customer Selection or Manual Entry */}
          {!selectedCustomer && !manualMode ? (
            <CustomerPicker
              onSelect={handleCustomerSelect}
              onManualMode={() => setManualMode(true)}
            />
          ) : (
            <>
              {/* Selected customer badge or manual mode indicator */}
              {selectedCustomer && (
                <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg" style={{ backgroundColor: '#f0ebe5' }}>
                  <div className="flex-1">
                    <span className="text-sm font-semibold" style={{ color: '#4a2c1a' }}>{selectedCustomer.name}</span>
                    <span className="text-xs ml-2" style={{ color: '#736c64' }}>{selectedCustomer.email}</span>
                  </div>
                  <button
                    data-testid="change-customer-btn"
                    onClick={resetEmailForm}
                    className="text-xs font-semibold underline"
                    style={{ color: '#FF8C19' }}
                  >
                    Change
                  </button>
                </div>
              )}
              {manualMode && !selectedCustomer && (
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-sm" style={{ color: '#736c64' }}>Manual entry</p>
                  <button
                    onClick={() => { setManualMode(false); resetEmailForm(); }}
                    className="text-xs font-semibold underline"
                    style={{ color: '#FF8C19' }}
                  >
                    Search customers instead
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <Input
                  data-testid="review-req-name"
                  placeholder="Customer name"
                  value={emailData.customerName}
                  onChange={(e) => setEmailData({ ...emailData, customerName: e.target.value })}
                />
                <Input
                  data-testid="review-req-email"
                  type="email"
                  placeholder="Customer email"
                  value={emailData.email}
                  onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                />
                {/* Order # dropdown for selected customer with orders, or plain input */}
                {selectedCustomer && selectedCustomer.orders.length > 1 ? (
                  <select
                    data-testid="review-req-order"
                    value={emailData.orderNumber}
                    onChange={(e) => setEmailData({ ...emailData, orderNumber: e.target.value })}
                    className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                    style={{ borderColor: '#e5d5c0' }}
                  >
                    <option value="">No order #</option>
                    {selectedCustomer.orders.map(o => (
                      <option key={o} value={o}>#{o}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    data-testid="review-req-order"
                    placeholder="Order # (optional)"
                    value={emailData.orderNumber}
                    onChange={(e) => setEmailData({ ...emailData, orderNumber: e.target.value })}
                  />
                )}
              </div>
              <div className="flex gap-3">
                <Button data-testid="send-review-email-btn" onClick={sendReviewRequest} disabled={sendingEmail || !emailData.email || !emailData.customerName} className="text-white" style={{ backgroundColor: '#FF8C19' }}>
                  {sendingEmail ? 'Sending...' : 'Send Request'}
                </Button>
                <Button variant="outline" onClick={() => { setShowEmailForm(false); resetEmailForm(); }}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: 'all', label: `All (${reviews.length})` },
          { key: 'pending', label: `Pending (${pendingCount})` },
          { key: 'approved', label: `Approved (${reviews.length - pendingCount})` }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className="px-4 py-1.5 rounded-full text-sm font-semibold transition-colors"
            style={{
              backgroundColor: filter === f.key ? '#FF8C19' : '#f7f5f2',
              color: filter === f.key ? 'white' : '#4a2c1a'
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Reviews List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-10 text-center">
          <Star className="h-12 w-12 mx-auto mb-3" style={{ color: '#e5d5c0' }} />
          <p className="text-lg font-bold" style={{ color: '#4a2c1a' }}>No reviews found</p>
          <p style={{ color: '#736c64' }}>Send review request emails to your customers to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(review => (
            <div
              key={review.id}
              className="bg-white rounded-lg shadow-sm p-5 border-l-4 transition-all hover:shadow-md"
              style={{ borderColor: review.approved ? '#22c55e' : '#f59e0b' }}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-bold" style={{ color: '#4a2c1a' }}>{review.customerName}</span>
                    <StarDisplay rating={review.rating} />
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1"
                      style={{
                        backgroundColor: review.approved ? '#d1fae5' : '#fef3c7',
                        color: review.approved ? '#065f46' : '#92400e'
                      }}
                    >
                      {review.approved ? <><Eye className="h-3 w-3" /> Live</> : <><Clock className="h-3 w-3" /> Pending</>}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: '#736c64' }}>
                    {review.customerEmail} · {new Date(review.createdAt).toLocaleDateString()}
                    {review.orderNumber && ` · Order #${review.orderNumber}`}
                  </p>
                </div>
              </div>

              {review.title && <h4 className="font-semibold mb-1" style={{ color: '#4a2c1a' }}>{review.title}</h4>}
              <p className="text-sm mb-3" style={{ color: '#4a2c1a' }}>{review.text}</p>

              {review.photos && review.photos.length > 0 && (
                <div className="flex gap-2 mb-3">
                  {review.photos.map((photo, i) => (
                    <img key={i} src={photo} alt="" className="w-16 h-16 object-cover rounded-lg border" style={{ borderColor: '#e5d5c0' }} />
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                {!review.approved && (
                  <Button size="sm" onClick={() => approveReview(review.id)} className="text-white" style={{ backgroundColor: '#22c55e' }}>
                    <Check className="h-4 w-4 mr-1" /> Approve
                  </Button>
                )}
                {review.approved && (
                  <Button size="sm" variant="outline" onClick={() => rejectReview(review.id)} style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
                    <X className="h-4 w-4 mr-1" /> Hide
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => deleteReview(review.id)} style={{ borderColor: '#dc2626', color: '#dc2626' }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsTab;
